import {Game} from "@game/game";
import {connect} from "react-redux";
import {Dialog} from "@components/interface/Dialog";
import {selectDialog} from "@redux/selectors/dialog";
import {LocalisedTypography} from "@components/localisation/LocalisedTypography";
import {IDialog} from "@redux/slices/dialog";
import {update as updateDialog} from "@redux/slices/dialog";
import React, {CSSProperties, useEffect, useState} from "react";
import {Update} from "@redux/slices/base";
import {Tabs} from "@components/interface/Tabs";
import {TabsPanel} from "@components/interface/TabsPanel";
import {DebugTabPanel} from "@components/dialog/game/DebugTabPanel";
import {UserInvitesPanel} from "@components/dialog/game/UserInvitesPanel";
import {AchievementsTabPanel} from "@components/dialog/game/AchievementsTabPanel";
import {THEME} from "@components/core/theme";
import Version from "@data/version.json";

const PatreonLink = "https://patreon.com/wordland";

const styles: Record<string, CSSProperties> = {
    ...THEME,
    paragraph: {
        marginTop: 8
    },
    version: {
        ...THEME.text,
        color: "grey",
        fontSize: "x-small",
        fontWeight: "bold"
    }
}

interface IGameDialog {
    dialog: IDialog
    game: Game
    updateDialog: Update
}
function GameDialog({dialog, game, updateDialog}: IGameDialog) {
    const [roles, setRoles] = useState<string[]>([]);
    const [installPrompt, setInstallPrompt] = useState<Event | null>(null);

    useEffect(() => {
        const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e); };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const handleInstall = () => {
        (installPrompt as any).prompt();
        (installPrompt as any).userChoice.then(() => setInstallPrompt(null));
    };

    const handleLogout = () => {
        game.logout();
        updateDialog({update: {}});
    };
    const handleLogin = () => {
        updateDialog({update: {name: "authenticate"}});
    };
    const handleHowToPlay = () => {
        updateDialog({update: {name: "tutorial"}});
    };
    const handleSetNickname = () => {
        localStorage.setItem("deferNickname", "false");
        window.location.reload();
    };

    useEffect(() => {
        game.getUserRoles().then(roles => setRoles(roles));
    }, []);
    if (dialog.name !== "game") return null;
    return (
        <Dialog close={true} title="GameMenuTitle">
            <Tabs initial={"Welcome"}>
                <TabsPanel label={"Welcome"}>
                    <LocalisedTypography content="WelcomeText" style={styles.paragraph}/>
                    {game.perks.noAdverts
                        ? <LocalisedTypography content="WelcomePatreonConnected" style={styles.paragraph}/>
                        : <>
                            <LocalisedTypography content="WelcomePatreonText" style={styles.paragraph}/>
                            <div style={{...styles.paragraph, flexGrow: 1}}>
                                <a href={PatreonLink} target="Patreon">
                                    <LocalisedTypography content="WelcomePatreonLink"/>
                                </a>
                            </div>
                          </>
                    }
                    <div style={styles.buttons}>
                        <button onClick={handleHowToPlay} style={styles.button}>
                            <LocalisedTypography content={"ButtonHowToPlay"}/>
                        </button>
                        {installPrompt &&
                            <button onClick={handleInstall} style={styles.button}>
                                <LocalisedTypography content={"ButtonInstallApp"}/>
                            </button>}
                        {!game.userPlayer?.anonymous && !game.userPlayer?.hasNickname() &&
                            <button onClick={handleSetNickname} style={styles.button}>
                                <LocalisedTypography content={"ButtonSetNickname"}/>
                            </button>}
                        {game.userPlayer?.anonymous
                            ? <button onClick={handleLogin} style={styles.button}>
                                <LocalisedTypography content={"ButtonLogin"}/>
                              </button>
                            : <button onClick={handleLogout} style={styles.button}>
                                <LocalisedTypography content={"ButtonLogout"}/>
                              </button>
                        }
                    </div>
                    {!game.userPlayer?.anonymous
                        ? <p style={{...THEME.text, fontSize: "small", marginTop: 8}}>
                            {`Logged in as ${game.userPlayer?.username} with nickname ${game.userPlayer?.getNickname()}`}
                          </p>
                        : <></>
                    }
                </TabsPanel>
                {!game.userPlayer?.anonymous ?
                    <TabsPanel label={"Achievements"}>
                        <AchievementsTabPanel game={game}/>
                    </TabsPanel> : undefined
                }
                {roles.includes("tester") ?
                    <TabsPanel label={"Debug"}>
                        <DebugTabPanel/>
                    </TabsPanel> : undefined
                }
                {roles.includes("admin") ?
                    <TabsPanel label={"Invites"}>
                        <UserInvitesPanel game={game}/>
                    </TabsPanel> : undefined
                }
            </Tabs>
            <LocalisedTypography content="GameVersion" params={Version} style={styles.version}/>
        </Dialog>
    );
}

const mapStateToProps = (store: never) => ({
    dialog: selectDialog(store)
});
const connected = connect(mapStateToProps, {
    updateDialog
})(GameDialog);
export {connected as GameDialog}
